import {
  Divider,
  Dropdown,
  IconButton,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  Typography,
} from '@mui/joy';
import {
  ArrowRight,
  ArrowUpRight,
  Calendar,
  Clipboard as ClipboardIcon,
  FileText,
  FloppyDisk,
  Gear,
  Globe,
  List as ListIcon,
  SignOut,
  Tray,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link, Outlet } from 'react-router-dom';
import { useLocation } from 'react-use';

import CONFIDE_LOGO from '~/assets/svg/confideSidebarLogo.svg';
import { Drawer, DrawerContent, DrawerTrigger } from '~/components/ui/drawer';
import { NavItem, NavList } from '~/components/ui/navigation';
import { createSafeContext } from '~/components/ui/utils';
import { useLanguage } from '~/contexts/lang/languageContext';
import { useLocationSlug } from '~/features/tenant/helpers';
import { useToastNotificationContext } from '~/hooks/global/alert/useToastNotification';
import { useWrappedMutation } from '~/services/api/rest-api';
import { TenantService } from '~/services/api/tenant/endpoints';
import { UserService } from '~/services/api/users/endpoints';

type CollapsableSidebarProps = {
  collapsed: boolean;
  setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

const [Provider, useCollapsibleSidebar] =
  createSafeContext<CollapsableSidebarProps>(
    'useCollapsibleSidebar must be used within a SidebarProvider',
  );

export { useCollapsibleSidebar };

export function Layout() {
  const [collapsed, setCollapsed] = useState(true);

  const [open, setOpen] = useState(false);
  const pathname = useLocation().pathname;
  /**
   * closes the drawer when we navigate to anywhere
   */
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <section className='min-h-screen h-screen flex flex-col'>
      <header className='bg-brand-black sticky top-0 z-10'>
        <div className='w-full flex gap-2 items-center p-5 container'>
          <div className='sm:hidden'>
            <Drawer open={open} onOpenChange={setOpen}>
              <DrawerTrigger asChild>
                <IconButton color='primary'>
                  <ListIcon />
                </IconButton>
              </DrawerTrigger>
              <DrawerContent inheritStyles={false}>
                <Provider
                  value={{
                    collapsed: false,
                    setCollapsed: () => setOpen(false),
                  }}
                >
                  <Sidebar />
                </Provider>
              </DrawerContent>
            </Drawer>
          </div>
          <div className='flex gap-2 h-7 items-center'>
            <Link to='/inbox' className='h-full'>
              <img src={CONFIDE_LOGO} alt='Confide' className='h-full' />
            </Link>
          </div>
        </div>
      </header>
      <Provider value={{ collapsed, setCollapsed }}>
        <div className='flex flex-1 overflow-hidden'>
          <div className='hidden sm:flex h-full flex-col'>
            <Sidebar />
          </div>
          <main className='flex-1 overflow-auto p-2 sm:p-6'>
            <Outlet />
          </main>
        </div>
      </Provider>
    </section>
  );
}

export function SidebarProvider({
  children,
  values,
}: {
  children: React.ReactNode;
  values?: Partial<CollapsableSidebarProps>;
}) {
  const [collapsed, setCollapsed] = useState(
    values?.collapsed !== undefined ? values.collapsed : true,
  );

  return (
    <Provider
      value={{
        collapsed,
        setCollapsed:
          values?.setCollapsed !== undefined
            ? values.setCollapsed
            : setCollapsed,
      }}
    >
      {children}
    </Provider>
  );
}

function Sidebar() {
  const { collapsed, setCollapsed } = useCollapsibleSidebar();

  return (
    <aside
      className={clsx([
        'bg-brand-black p-2 gap-4 flex flex-col h-full',
        collapsed ? 'w-16 shrink-0' : 'w-64 shrink-0',
      ])}
      data-joy-color-scheme='dark'
      data-mui-color-scheme='dark'
    >
      <div
        className={clsx([
          'flex items-center gap-4 px-2',
          collapsed ? 'justify-center' : '',
        ])}
      >
        <IconButton
          color='primary'
          variant='soft'
          onClick={() => setCollapsed(!collapsed)}
        >
          <ListIcon />
        </IconButton>
        {!collapsed && <Typography level='h4'>Menu</Typography>}
      </div>
      <SidebarNavigation collapsed={collapsed} />
    </aside>
  );
}

export function SidebarNavigation({ collapsed }: { collapsed: boolean }) {
  return (
    <div className='h-full flex flex-col'>
      <NavList className='h-full' collapsed={collapsed}>
        <ListItem>
          <NavItem
            title='Inbox'
            startDecorator={<Tray />}
            href='/inbox'
            label='Inbox'
            collapsed={collapsed}
          />
        </ListItem>
        <ListItem>
          <NavItem
            startDecorator={<ClipboardIcon />}
            href='/new-report'
            label='Create Report'
            collapsed={collapsed}
          />
        </ListItem>
        <ListItem>
          <NavItem
            startDecorator={<FloppyDisk />}
            href='/drafts'
            label='Draft Report'
            collapsed={collapsed}
          />
        </ListItem>
        <ListItem>
          <NavItem
            startDecorator={<Calendar />}
            href='/meeting-list'
            label='Meetings'
            collapsed={collapsed}
          />
        </ListItem>
        <ListItem>
          <NavItem
            href='/knowledge-base'
            startDecorator={<FileText />}
            endDecorator={<ArrowUpRight />}
            label='Knowledgebase'
            collapsed={collapsed}
          />
        </ListItem>
        <ListItem>
          <NavItem
            startDecorator={<Gear />}
            href='/settings'
            label='Settings'
            collapsed={collapsed}
          />
        </ListItem>
        <div className='mt-auto'>
          <ListItem>
            <DisplayLanguageSelect />
          </ListItem>
        </div>
      </NavList>
      <Divider />
      <NavList collapsed={collapsed}>
        <ListItem>
          <NavItem
            href='/logout'
            collapsed={collapsed}
            startDecorator={<SignOut />}
            label='Logout'
          />
        </ListItem>
      </NavList>
    </div>
  );
}

function DisplayLanguageSelect() {
  const intl = useIntl();
  const { createToastNotification } = useToastNotificationContext();
  const { collapsed } = useCollapsibleSidebar();

  const flags = UserService.endpoints.reporterFlags.useQuery();
  const selectedLanguage = flags.data?.language_id ?? 0;
  const slug = useLocationSlug();

  const { setLanguage } = useLanguage();
  const languages = TenantService.endpoints.tenantPublicProgram.useQuery({
    tenantSlug: slug,
  }).data?.languages;

  const updateFlags = useWrappedMutation(
    UserService.endpoints.updateReporterFlags.useMutation(),
  );
  const onChangeHandler = async (langId: number) => {
    const language = languages?.find((lang) => lang.id === langId);
    if (!language) {
      return;
    }
    await updateFlags.mutateAsync(
      { language_id: langId },
      {
        onSuccess() {
          setLanguage(language);
          createToastNotification(
            intl.formatMessage({
              defaultMessage: 'Language changed successfully',
            }),
            'success',
          );
        },
      },
    );
  };
  return (
    <Dropdown>
      <MenuButton
        variant='plain'
        slots={{ root: 'span' }}
        className='inline-block w-full'
        disabled={updateFlags.state.isLoading}
      >
        <NavItem
          startDecorator={<Globe />}
          endDecorator={<ArrowRight />}
          label='Langauge'
          collapsed={collapsed}
        />
      </MenuButton>
      <Menu
        placement='right-start'
        sx={{
          minWidth: '9rem',
        }}
      >
        {languages?.map((language) => (
          <MenuItem
            key={language.id}
            selected={selectedLanguage === language.id}
            onClick={() => onChangeHandler(language.id)}
          >
            {language.name}
          </MenuItem>
        ))}
      </Menu>
    </Dropdown>
  );
}
